import { Subcategory } from '@company-box/models';

// Pure derivations behind the Material & Finish flyout (design-3d.component.ts) - ported directly
// from design.component.ts's updatePrintingOptions()/updateMaterialOptions()/updateFinishOptions()
// without pulling in any pricing/validation logic. Each function only computes option lists /
// derived flags from its inputs; the modal-driven two-sided-printing flow and the actual field
// mutation/orchestration stay in the component.

export function computePrintingOptions(meta: Subcategory): string[] {
  const printingOptions: string[] = [];
  if (meta.displayStandard) {
    printingOptions.push('Standard | Outside');
    if (meta.displayTwoSided) {
      printingOptions.push('Standard | Inside', 'Standard | 2-Sided');
    }
  }
  if (meta.displayLuxe) {
    printingOptions.push('Luxe | Outside');
    if (meta.displayTwoSided) {
      printingOptions.push('Luxe | Inside', 'Luxe | 2-Sided');
    }
  }
  return printingOptions;
}

export interface PrintingSelection {
  standard: boolean;
  luxe: boolean;
  insideOnly: boolean;
  twoSided: boolean;
}

export function parsePrintingSelection(printing: string): PrintingSelection {
  return {
    standard: printing.startsWith('Standard'),
    luxe: printing.startsWith('Luxe'),
    insideOnly: printing.endsWith('Inside'),
    twoSided: printing.endsWith('2-Sided'),
  };
}

export interface MaterialOptionsParams {
  meta: Subcategory;
  isPaddedMailer: boolean;
  categoryName: string;
  subcategory: string;
  innerMaterial: string;
  outerMaterial: string;
}

export interface MaterialOptions {
  outerMaterialOptions: string[];
  innerMaterialOptions: string[];
}

export function computeMaterialOptions(params: MaterialOptionsParams): MaterialOptions {
  const { meta, isPaddedMailer, categoryName, subcategory, innerMaterial, outerMaterial } = params;
  const outerMaterialOptions: string[] = [];
  const innerMaterialOptions: string[] = [];

  if (meta.displayWhite) {
    outerMaterialOptions.push(isPaddedMailer ? 'White' : 'Standard White');
    if (categoryName !== 'Event Boxes' && subcategory !== 'PIZ') {
      innerMaterialOptions.push('Standard White');
    }
  }
  if (meta.displayPremiumWhite) {
    if (innerMaterial !== 'Premium White' && subcategory !== 'PIZ') {
      outerMaterialOptions.push('Premium White');
    }
    if (outerMaterial !== 'Premium White' && categoryName !== 'Event Boxes' && subcategory !== 'PIZ') {
      innerMaterialOptions.push('Premium White');
    }
  }
  if (meta.displayKraft) {
    if (subcategory !== 'PIZ') {
      outerMaterialOptions.push('Kraft');
    }
    innerMaterialOptions.push('Kraft');
  }

  return { outerMaterialOptions, innerMaterialOptions };
}

export interface FinishOptionsParams {
  standard: boolean;
  luxe: boolean;
  printing: string;
  innerMaterial: string;
  outerMaterial: string;
}

export interface FinishOptions {
  // undefined means "leave the component's current finish untouched" - mirrors the original
  // updateFinishOptions()'s final else branch, which only ever set finishOptions, not finish.
  finish?: string;
  finishOptions: string[];
}

export function computeFinishOptions(params: FinishOptionsParams): FinishOptions {
  const { standard, luxe, printing, innerMaterial, outerMaterial } = params;
  let finish: string | undefined;
  let finishOptions: string[];

  if (luxe && printing === 'Luxe | Inside' && innerMaterial === 'Premium White') {
    finish = 'Gloss';
    finishOptions = ['Gloss'];
  } else if (luxe && printing === 'Luxe | Outside' && outerMaterial === 'Premium White') {
    finish = 'Gloss';
    finishOptions = ['Gloss'];
  } else if (luxe && (innerMaterial !== 'Premium White' || outerMaterial !== 'Premium White')) {
    finish = 'Matte';
    finishOptions = ['Matte'];
  } else {
    finishOptions = ['Matte'];
  }

  if (standard && (innerMaterial === 'Premium White' || outerMaterial === 'Premium White')) {
    finishOptions.push('Gloss');
  }

  return { finish, finishOptions };
}

const KRAFT_BOARD_COLOR = 0xb08d57;
const WHITE_BOARD_COLOR = 0xffffff;

// Tints the board's base material color - multiplies with the outside map (the live design
// texture) and the inside map (still the DAE placeholder), so a kraft choice reads as kraft on
// the box instead of the fixed white it rendered as before. Doesn't attempt to simulate how ink
// actually prints differently on kraft vs white stock - a flat tint, not a print simulation.
export function boardColorFor(material: string): number {
  return material === 'Kraft' ? KRAFT_BOARD_COLOR : WHITE_BOARD_COLOR;
}
